import { Route } from '@angular/router';
import { AccountBlockedComponent } from './auth/account-blocked/account-blocked.component';
import { authGuard } from './auth/auth.guard';
import {
  workspaceLandingGuard,
  workspaceRoleGuard,
} from './shell/workspace-role.guard';
import type { DashboardRouteData } from './shell/workspace-role.model';
import { WorkspaceChromeComponent } from './shell/workspace-chrome.component';
// B1 (Active Call Console) is NOT a routed screen — per UCWIP UI Build Spec
// Block B's intro paragraph, it's a persistent, summonable panel reachable
// from any screen, mounted once in app.html alongside <router-outlet>.
// Field mode (`/field/*`) and portals (`/portal/*`) use standalone chrome
// without the desktop workforce shell (no side nav, search, or role switcher).
const dashboardGuards = [workspaceRoleGuard];
const workforceGuards = [authGuard];

export const appRoutes: Route[] = [
  { path: 'account-blocked', component: AccountBlockedComponent },
  {
    path: '',
    component: WorkspaceChromeComponent,
    canActivateChild: workforceGuards,
    children: [
      // Component-less redirect: the landing guard needs to await auth init,
      // which a `redirectTo` function cannot do (it runs during recognition).
      {
        path: '',
        pathMatch: 'full',
        canActivate: [workspaceLandingGuard],
        children: [],
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import(
            './dashboards/intake-specialist-dashboard/intake-dashboard-page'
          ).then((m) => m.IntakeDashboardPage),
        canActivate: dashboardGuards,
        // CCWIS-174: the intake list is the only dashboard with two audiences.
        data: {
          allowedRoles: ['intake_specialist', 'administrator', 'supervisor'],
        } satisfies DashboardRouteData,
      },
      {
        path: 'investigations/:investigationId/disposition',
        redirectTo: 'investigations/:investigationId/sdm-allegations',
      },
      {
        path: 'investigator-dashboard',
        loadComponent: () =>
          import(
            './dashboards/investigator-dashboard/investigator-dashboard-page'
          ).then((m) => m.InvestigatorDashboardPage),
        canActivate: dashboardGuards,
      },
      {
        path: 'caseworker-dashboard',
        loadComponent: () =>
          import('./dashboards/caseworker-dashboard/caseworker-dashboard').then(
            (m) => m.CaseworkerDashboard,
          ),
        canActivate: dashboardGuards,
      },
      {
        path: 'supervisor-dashboard',
        loadComponent: () =>
          import(
            './dashboards/supervisor-dashboard/supervisor-dashboard-page'
          ).then((m) => m.SupervisorDashboardPage),
        canActivate: dashboardGuards,
      },
      {
        path: 'program-manager-dashboard',
        loadComponent: () =>
          import(
            './dashboards/program-manager-dashboard/program-manager-dashboard'
          ).then((m) => m.ProgramManagerDashboard),
        canActivate: dashboardGuards,
      },
      {
        path: 'search',
        loadComponent: () => import('./search').then((m) => m.SearchPage),
      },
      {
        path: 'persons/:personId',
        loadComponent: () =>
          import('./persons/person-detail-placeholder').then(
            (m) => m.PersonDetailPlaceholder,
          ),
      },
      {
        path: 'referrals/:referralId',
        loadComponent: () =>
          import('./referrals/referral-detail/referral-detail').then(
            (m) => m.ReferralDetail,
          ),
      },
      {
        path: 'investigations/:investigationId',
        loadComponent: () =>
          import(
            './investigations/investigation-intake-case-shell/investigation-intake-case-shell'
          ).then((m) => m.InvestigationIntakeCaseShell),
        children: [
          { path: '', redirectTo: 'narrative', pathMatch: 'full' },
          {
            path: 'narrative',
            loadComponent: () =>
              import(
                './investigations/investigation-record/investigation-record'
              ).then((m) => m.InvestigationRecord),
          },
          {
            path: 'transfer-packet',
            loadComponent: () =>
              import(
                './investigations/transfer-packet/transfer-packet-form'
              ).then((m) => m.TransferPacketForm),
          },
          {
            path: 'family-household',
            loadComponent: () =>
              import('./cases/family-household-tab/family-household-tab').then(
                (m) => m.FamilyHouseholdTab,
              ),
          },
          {
            path: 'assessments',
            loadComponent: () =>
              import(
                './investigations/investigation-assessments-tab/investigation-assessments-tab'
              ).then((m) => m.InvestigationAssessmentsTab),
          },
          {
            path: 'contacts',
            loadComponent: () =>
              import(
                './cases/investigation-contacts-tab/investigation-contacts-tab'
              ).then((m) => m.InvestigationContactsTab),
          },
          {
            path: 'documents',
            loadComponent: () =>
              import(
                './investigations/investigation-tab-placeholder/investigation-tab-placeholder'
              ).then((m) => m.InvestigationTabPlaceholder),
            data: { tabTitle: 'Document' },
          },
          {
            path: 'sdm-allegations',
            loadComponent: () =>
              import(
                './investigations/allegation-disposition/allegation-disposition-page'
              ).then((m) => m.AllegationDispositionPage),
            data: { embedded: true },
          },
          {
            path: 'decision',
            loadComponent: () =>
              import('./investigations/completeness-check').then(
                (m) => m.CompletenessCheck,
              ),
          },
        ],
      },
      {
        path: 'investigations/:investigationId/completeness',
        loadComponent: () =>
          import('./investigations/completeness-check').then(
            (m) => m.CompletenessCheck,
          ),
      },
      {
        path: 'investigations/:investigationId/sections/:sectionId',
        loadComponent: () =>
          import('./investigations/investigation-section').then(
            (m) => m.InvestigationSection,
          ),
      },
      {
        path: 'investigations/:investigationId/present-danger-plan/child/:childPersonId',
        loadComponent: () =>
          import(
            './investigations/present-danger-plan/present-danger-plan-entry.page'
          ).then((m) => m.PresentDangerPlanEntryPage),
      },
      {
        path: 'investigations/:investigationId/present-danger-plan/:planId',
        loadComponent: () =>
          import(
            './investigations/present-danger-plan/present-danger-plan-page'
          ).then((m) => m.PresentDangerPlanPage),
      },
      {
        path: 'cases/:caseId',
        loadComponent: () =>
          import('./cases/case-record-shell/case-record-shell').then(
            (m) => m.CaseRecordShell,
          ),
        children: [
          { path: '', redirectTo: 'overview', pathMatch: 'full' },
          {
            path: 'overview',
            loadComponent: () =>
              import('./cases/case-overview-tab/case-overview-tab').then(
                (m) => m.CaseOverviewTab,
              ),
          },
          {
            path: 'family-household',
            loadComponent: () =>
              import('./cases/family-household-tab/family-household-tab').then(
                (m) => m.FamilyHouseholdTab,
              ),
          },
          {
            path: 'investigation-history',
            loadComponent: () =>
              import(
                './cases/investigation-history-tab/investigation-history-tab'
              ).then((m) => m.InvestigationHistoryTab),
          },
          {
            path: 'contacts',
            loadComponent: () =>
              import(
                './cases/investigation-contacts-tab/investigation-contacts-tab'
              ).then((m) => m.InvestigationContactsTab),
          },
          {
            path: 'court',
            loadComponent: () =>
              import('./cases/court-tab/court-tab').then((m) => m.CourtTab),
          },
          {
            path: 'review',
            loadComponent: () =>
              import('./cases/supervisor-review/supervisor-review-tab').then(
                (m) => m.SupervisorReviewTab,
              ),
          },
          {
            path: 'transfer-review',
            loadComponent: () =>
              import('./cases/transfer-review/transfer-review-page').then(
                (m) => m.TransferReviewPage,
              ),
          },
        ],
      },
      {
        path: 'cases/:caseId/transfer-review',
        loadComponent: () =>
          import('./cases/transfer-review/transfer-review-page').then(
            (m) => m.TransferReviewPage,
          ),
        canActivate: dashboardGuards,
        data: {
          allowedRoles: ['supervisor', 'administrator'],
        } satisfies DashboardRouteData,
      },
      {
        path: 'workers/:workerId',
        loadComponent: () =>
          import('./workers/worker-caseload-placeholder').then(
            (m) => m.WorkerCaseloadPlaceholder,
          ),
      },
      {
        path: 'design-first/court-signature-gap',
        loadComponent: () =>
          import('./design-first/court-signature-gap/court-signature-gap').then(
            (m) => m.CourtSignatureGap,
          ),
      },
    ],
  },
  {
    path: 'field',
    loadComponent: () =>
      import('./field/field-mode-shell.component').then(
        (m) => m.FieldModeShellComponent,
      ),
    canActivate: workforceGuards,
    canActivateChild: workforceGuards,
    children: [
      { path: '', redirectTo: 'my-day', pathMatch: 'full' },
      {
        path: 'my-day',
        loadComponent: () =>
          import('./field/my-day/my-day-lofi.component').then(
            (m) => m.MyDayLofiComponent,
          ),
      },
      {
        path: 'cases',
        loadComponent: () =>
          import('./field/field-cases-stub/field-cases-stub.component').then(
            (m) => m.FieldCasesStubComponent,
          ),
      },
      {
        path: 'capture',
        loadComponent: () =>
          import('./field/visit-capture/visit-capture-lofi.component').then(
            (m) => m.VisitCaptureLofiComponent,
          ),
      },
      {
        path: 'capture/:visitId',
        loadComponent: () =>
          import('./field/visit-capture/visit-capture-route.component').then(
            (m) => m.VisitCaptureRouteComponent,
          ),
      },
      {
        path: 'outbox',
        loadComponent: () =>
          import('./field/field-outbox-stub/field-outbox-stub.component').then(
            (m) => m.FieldOutboxStubComponent,
          ),
      },
    ],
  },
  {
    // Section 3 lo-fi portals (CCWIS-61) — separate chrome from workforce shell.
    // Walkthrough entry affordance (link from workspace) is an open question,
    // same as field mode; do not promote parent/fosterfamily as workforce roles.
    path: 'portal',
    loadComponent: () =>
      import('./portals/portal-chrome.component').then(
        (m) => m.PortalChromeComponent,
      ),
    children: [
      { path: '', redirectTo: 'parent', pathMatch: 'full' },
      {
        path: 'parent',
        loadComponent: () =>
          import('./portals/parent-portal-home/parent-portal-home').then(
            (m) => m.ParentPortalHome,
          ),
      },
      {
        path: 'foster-family',
        loadComponent: () =>
          import(
            './portals/foster-family-portal-home/foster-family-portal-home'
          ).then((m) => m.FosterFamilyPortalHome),
      },
    ],
  },
];
